﻿export function initViwoColorPickers(root = document) {
    const pickers = Array.from(root.querySelectorAll("[data-viwo-color-picker]"));

    pickers.forEach(picker => {
        if (picker.dataset.viwoColorPickerInitialized === "true") return;

        picker.dataset.viwoColorPickerInitialized = "true";

        const colorInput = picker.querySelector("input[type='color']");
        const hexInput = picker.querySelector("[data-viwo-color-hex]");
        const preview = picker.querySelector("[data-viwo-color-preview]");
        const copyButton = picker.querySelector("[data-viwo-color-copy]");

        if (!colorInput) return;

        const initialColor = normalizeHex(colorInput.value || hexInput?.value || "#6366f1");

        picker.dataset.currentColor = initialColor;

        commitPickerColor({
            picker,
            colorInput,
            hexInput,
            preview,
            value: initialColor,
            notify: false
        });

        colorInput.addEventListener("input", () => {
            if (picker.dataset.internalColorUpdate === "true") return;

            commitPickerColor({
                picker,
                colorInput,
                hexInput,
                preview,
                value: colorInput.value,
                notify: true
            });
        });

        colorInput.addEventListener("change", () => {
            if (picker.dataset.internalColorUpdate === "true") return;

            commitPickerColor({
                picker,
                colorInput,
                hexInput,
                preview,
                value: colorInput.value,
                notify: true
            });
        });

        hexInput?.addEventListener("input", () => {
            const raw = hexInput.value.trim();

            picker.classList.add("is-editing");

            if (!raw) {
                picker.classList.remove("is-invalid");
                return;
            }

            const isPotential =
                isPotentialHex(raw) ||
                isPotentialHex(`#${raw}`);

            picker.classList.toggle("is-invalid", !isPotential);

            if (!isPotential) return;

            const isComplete =
                isCompleteHex(raw) ||
                isCompleteHex(`#${raw}`);

            if (!isComplete) return;

            commitPickerColor({
                picker,
                colorInput,
                hexInput,
                preview,
                value: raw,
                notify: true,
                updateHexInput: false
            });
        });

        hexInput?.addEventListener("blur", () => {
            picker.classList.remove("is-editing");

            const raw = hexInput.value.trim();

            if (!raw) {
                commitPickerColor({
                    picker,
                    colorInput,
                    hexInput,
                    preview,
                    value: picker.dataset.currentColor || colorInput.value,
                    notify: false
                });
                return;
            }

            const isComplete =
                isCompleteHex(raw) ||
                isCompleteHex(`#${raw}`);

            if (!isComplete) {
                picker.classList.remove("is-invalid");

                commitPickerColor({
                    picker,
                    colorInput,
                    hexInput,
                    preview,
                    value: picker.dataset.currentColor || colorInput.value,
                    notify: false
                });
                return;
            }

            commitPickerColor({
                picker,
                colorInput,
                hexInput,
                preview,
                value: raw,
                notify: true
            });
        });

        hexInput?.addEventListener("keydown", event => {
            if (event.key === "Enter") {
                event.preventDefault();
                hexInput.blur();
            }

            if (event.key === "Escape") {
                event.preventDefault();

                picker.classList.remove("is-invalid", "is-editing");

                commitPickerColor({
                    picker,
                    colorInput,
                    hexInput,
                    preview,
                    value: picker.dataset.currentColor || colorInput.value,
                    notify: false
                });

                hexInput.blur();
            }
        });

        copyButton?.addEventListener("click", async event => {
            event.preventDefault();
            event.stopPropagation();

            const color = normalizeHex(picker.dataset.currentColor || colorInput.value);

            try {
                await navigator.clipboard.writeText(color);
                showCopiedState(picker, copyButton);
            } catch {
                fallbackCopy(color);
                showCopiedState(picker, copyButton);
            }
        });
    });
}

function commitPickerColor({
    picker,
    colorInput,
    hexInput,
    preview,
    value,
    notify,
    updateHexInput = true
}) {
    const color = normalizeHex(value);

    picker.dataset.currentColor = color;
    picker.classList.remove("is-invalid");
    picker.style.setProperty("--picker-color", color);

    // DOM value update sırasında kendi listenerlarımız çalışmasın
    picker.dataset.internalColorUpdate = "true";

    colorInput.value = toNativeColor(color);

    if (updateHexInput && hexInput) {
        hexInput.value = color;
    }

    if (preview) {
        preview.style.background = color;
    }

    if (notify) {
        colorInput.dispatchEvent(new CustomEvent("viwo:color-change", {
            bubbles: true,
            detail: {
                color
            }
        }));
    }

    // Dispatch bittikten sonra guard kapansın
    picker.dataset.internalColorUpdate = "false";
}

function normalizeHex(value) {
    let color = String(value || "").trim();

    if (!color) return "#000000";

    if (!color.startsWith("#")) {
        color = `#${color}`;
    }

    if (/^#[0-9a-fA-F]{3}$/.test(color)) {
        const r = color[1];
        const g = color[2];
        const b = color[3];

        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    }

    if (/^#[0-9a-fA-F]{6}$/.test(color)) {
        return color.toUpperCase();
    }

    if (/^#[0-9a-fA-F]{8}$/.test(color)) {
        return color.toUpperCase();
    }

    return "#000000";
}

function toNativeColor(value) {
    const color = normalizeHex(value);

    if (/^#[0-9a-fA-F]{8}$/.test(color)) {
        return color.slice(0, 7);
    }

    return color;
}

function isCompleteHex(value) {
    const color = String(value || "").trim();

    return /^#[0-9a-fA-F]{3}$/.test(color) ||
        /^#[0-9a-fA-F]{6}$/.test(color) ||
        /^#[0-9a-fA-F]{8}$/.test(color);
}

function isPotentialHex(value) {
    const color = String(value || "").trim();

    return /^#?[0-9a-fA-F]{0,8}$/.test(color);
}

function showCopiedState(picker, button) {
    picker.classList.add("is-copied");

    const oldHtml = button.innerHTML;
    button.innerHTML = `<i class="fa-regular fa-check"></i>`;

    window.setTimeout(() => {
        button.innerHTML = oldHtml;
        picker.classList.remove("is-copied");
    }, 900);
}

function fallbackCopy(value) {
    const input = document.createElement("input");
    input.value = value;
    input.style.position = "fixed";
    input.style.opacity = "0";
    input.style.pointerEvents = "none";

    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    input.remove();
}